import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article";
  keywords?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  hreflang?: Array<{ lang: string; url: string }>;
  preload?: Array<{ href: string; as: string; type?: string }>;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  keywords,
  jsonLd,
  hreflang,
  preload,
}) => {
  const url = canonical || (typeof window !== "undefined" ? window.location.href : "");
  const siteName = "SOL-VI";

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      
      {/* Preload Resources */}
      {preload?.map((resource, idx) => (
        <link
          key={idx}
          rel="preload"
          href={resource.href}
          as={resource.as}
          type={resource.type}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="nl_NL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {/* Robots & SEO */}
      <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      <meta name="googlebot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      
      {/* Hreflang */}
      {hreflang?.map((lang, idx) => (
        <link key={idx} rel="alternate" hrefLang={lang.lang} href={lang.url} />
      ))}

      {jsonLdArray.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
